import { useEffect, useState } from 'react'
import { showNotification } from 'summon-ui'
import { useTranslation } from 'summon-ui/intl'
import { useQueryClient } from '@tanstack/react-query'
import { graphqlClient } from '@api/config'
import { TENANT_CONTRACTS } from '@config'
import { LOOTDROP_ABI } from '@constants/Abi/lootDropAbi'
import { useRedeemRewardMutation, useUpdateRewardRedeemedMutation } from '@generated/generates'
import { useAddOnchainHistoryLog, useInventoryItems, useMintTypeBased } from '@hooks'
import { MintType } from '@hooks/useMintTypeBased'
import { isErrorResponse } from '@utils/errors'
import { isStaging } from '@utils/isStaging'

export const DEFAULT_ERROR_MESSAGE = 'Something went wrong'
export const SUCCESS_CLAIM_MESSAGE = 'Rewards claimed successfully!'

interface Props {
  redeemableId: string
  handleCloseMintModal?: () => void
  mintType?: MintType
  isSponsored?: boolean
}

const useRedeemable = ({ redeemableId, handleCloseMintModal, mintType = MintType?.Eth, isSponsored }: Props) => {
  const queryClient = useQueryClient()
  const t = useTranslation()
  const { addOnchainHistoryLog } = useAddOnchainHistoryLog()
  const [nonce, setNonce] = useState<null | undefined | number>(null)
  const [signature, setSignature] = useState<null | undefined | string>(null)
  const [seed, setSeed] = useState<null | undefined | string>(null)
  const [isRedeeming, setIsRedeeming] = useState(false)
  const { refetchInventoryItemsByIds, refetchUserOnchainItems } = useInventoryItems({})

  const invalidateGetRewardsRedeemedQuery = () => queryClient.invalidateQueries({ queryKey: ['GetRewardsRedeemed'] })

  const { mint } = useMintTypeBased({
    mintType,
    functionName: 'mint',
    args: [seed, true, nonce, signature, true],
    contract: isStaging ? TENANT_CONTRACTS.testnet.lootdrop : TENANT_CONTRACTS.mainnet.lootdrop,
    abi: LOOTDROP_ABI,
    isMainnet: !isStaging
  })

  const { mutateAsync: redeemMutation, isPending: isRedeemMutationLoading } = useRedeemRewardMutation(graphqlClient(), {
    onSuccess: (data) => {
      const dataToMint = data?.redeemReward?.encodeData
      if (dataToMint) {
        setNonce(dataToMint?.nonce)
        setSignature(dataToMint?.signature)
        setSeed(dataToMint?.seed)
      }
    }
  })

  const { mutateAsync: markRedeemableAsRedeemed } = useUpdateRewardRedeemedMutation(graphqlClient(), {})

  useEffect(() => {
    if (!isSponsored) {
      // if the tx is not sponsored we call the mutation to get the data we need to mint
      const getDataToMint = async () => {
        await redeemMutation({ input: { rewardsRedeemedId: redeemableId } })
      }
      getDataToMint()
    }
  }, [isSponsored])

  const redeem = async () => {
    try {
      setIsRedeeming(true)
      const tx = await mint()

      await addOnchainHistoryLog({
        txAddress: tx.transactionHash,
        contractAddress: tx.to,
        description: 'Reward  claimed'
      })

      await markRedeemableAsRedeemed({ input: { id: redeemableId } })

      showNotification({
        variant: 'success',
        message: SUCCESS_CLAIM_MESSAGE
      })
    } catch (error: unknown) {
      let errorMessage = DEFAULT_ERROR_MESSAGE
      if (isErrorResponse(error)) {
        {
          errorMessage = error.response?.errors?.[0]?.message || t(DEFAULT_ERROR_MESSAGE)
        }
        showNotification({
          variant: 'danger',
          message: errorMessage
        })
      }
    } finally {
      setIsRedeeming(false)
      handleCloseMintModal && handleCloseMintModal()
      invalidateGetRewardsRedeemedQuery()
      await refetchUserOnchainItems()
      await refetchInventoryItemsByIds()
    }
  }

  const redeemSponsored = async () => {
    try {
      setIsRedeeming(true)
      const response = await redeemMutation({ input: { rewardsRedeemedId: redeemableId } })
      const mintReceipt = response?.redeemReward?.mintReceipt

      if (mintReceipt) {
        await addOnchainHistoryLog({
          txAddress: mintReceipt?.tx,
          contractAddress: mintReceipt?.contractAddress,
          description: 'Reward  Claimed',
          explorerLink: mintReceipt?.explorerLink
        })
        showNotification({
          variant: 'success',
          message: SUCCESS_CLAIM_MESSAGE
        })
      }
    } catch (error: unknown) {
      let errorMessage = DEFAULT_ERROR_MESSAGE
      if (isErrorResponse(error)) {
        {
          errorMessage = error.response?.errors?.[0]?.message || t(DEFAULT_ERROR_MESSAGE)
        }
        showNotification({
          variant: 'danger',
          message: errorMessage
        })
      }
    } finally {
      setIsRedeeming(false)
      handleCloseMintModal && handleCloseMintModal()
      invalidateGetRewardsRedeemedQuery()
    }
  }

  return {
    redeemMutation,
    isRedeemMutationLoading,
    redeemSponsored,
    redeem,
    isRedeeming
  }
}

export default useRedeemable
